import { Map, MessageSquareShare, Loader2, Phone, Printer, ContactRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { FieldLabel } from './airport-ui';
import { BAR_ACTION_CLASS, BAR_GROUP_CLASS } from './airport-tokens';
import { usePnrUtilities } from './use-pnr-utilities';

// Utility actions for one PNR (drawer Care tab): the paper-and-comms half of
// working a passenger — printable vouchers (the unified travel pack, or the
// flight / hotel / directions sheets on their own), resending the info over a
// chosen channel, and correcting a wrong phone/email. Print sheets open as a
// dialog over the drawer and auto-trigger the browser print dialog; comms ride
// the notifications domain re-dispatch; contact rides the cases stamped write. Saving a contact correction chains straight into a resend
// prompt (the corrected value only takes effect on the NEXT send — the
// re-dispatch resolves contact just-in-time behind `recipientRef`).
//
// THE PACK IS THE DEFAULT HAND-OUT. A re-accommodated passenger with an
// overnight has a flight, a hotel, a loaded card and (maybe) a car — four sheets
// that each stop where the next one starts. `travel-pack` prints that night as
// one document, so the primary button here prints EVERYTHING this PNR has and
// the three single sheets stay as the reprint path (a front desk that wants only
// the hotel voucher, a counter that wants only the flight).
//
// PRINTING NEVER LEAVES THIS TAB. These buttons used to `window.open` a `/print`
// route in a new tab. Under this app's Auth0 setup that tab can never be
// authenticated — see the header of `pnr-print-sheet.tsx` — so the operator got
// a login screen instead of a voucher. The sheet is now a dialog rendered right
// here, in the tab that already holds the token.

interface PnrUtilityActionsProps {
  group: DisruptionPnrGroupRow;
  /** A live (non-terminal) hotel reservation exists — enables the hotel sheets. */
  hasHotelReservation: boolean;
  /** `card` (default) draws the boxed section with its heading; `toolbar` is the
   *  bare button row a tab pins above its cards; `bar` is the compact strip the
   *  operation center's botonera embeds (always visible, `BAR_ACTION_CLASS`). */
  variant?: 'card' | 'toolbar' | 'bar';
  /** Which buttons: `comms` = travel pack · send info · call · update contact
   *  (the always-visible set); `sheets` = the single vouchers + directions;
   *  `all` (default) = everything. */
  subset?: 'all' | 'comms' | 'sheets';
}

export function PnrUtilityActions({
  group,
  hasHotelReservation,
  variant = 'card',
  subset = 'all',
}: PnrUtilityActionsProps) {
  const { t } = useTranslation('pnrActions');
  const u = usePnrUtilities(group, hasHotelReservation);
  const { hasFlight, hasAnythingToPrint, runResend, runCall } = u;
  const compact = variant === 'bar';
  const cls = compact ? BAR_ACTION_CLASS : undefined;
  const showComms = subset !== 'sheets';
  const showSheets = subset !== 'comms';

  return (
    <section
      className={variant === 'card' ? 'rounded-lg border border-border p-3' : 'contents'}
      aria-label={t('utility.sectionLabel')}
      data-testid="pnr-utility-actions"
    >
      {variant === 'card' ? (
        <FieldLabel className="mb-2.5 flex items-center gap-1.5">
          <Printer className="h-3.5 w-3.5" aria-hidden />
          {t('utility.heading')}
        </FieldLabel>
      ) : null}
      <div className={compact ? BAR_GROUP_CLASS : 'flex flex-wrap gap-2'}>
        {showComms ? (
        <Button
          size="sm"
          variant={compact ? 'ghost' : 'default'}
          {...(cls ? { className: cls } : {})}
          disabled={!hasAnythingToPrint}
          title={
            hasAnythingToPrint
              ? t('utility.print.travelPackHint')
              : t('utility.print.nothingAssigned')
          }
          onClick={() => u.print('travel-pack')}
          data-testid="pnr-print-travel-pack"
        >
          <Printer className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('utility.print.travelPack')}
        </Button>
        ) : null}
        {showSheets ? (
        <>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasFlight}
          title={
            hasFlight ? t('utility.print.flightVoucherHint') : t('utility.print.noFlight')
          }
          onClick={() => u.print('flight-voucher')}
          data-testid="pnr-print-flight-voucher"
        >
          <Printer className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('utility.print.flightVoucher')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasHotelReservation}
          title={
            hasHotelReservation ? t('utility.print.hotelVoucherHint') : t('utility.print.noHotel')
          }
          onClick={() => u.print('hotel-voucher')}
          data-testid="pnr-print-hotel-voucher"
        >
          <Printer className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('utility.print.hotelVoucher')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasHotelReservation}
          title={
            hasHotelReservation ? t('utility.print.directionsHint') : t('utility.print.noHotel')
          }
          onClick={() => u.print('hotel-directions')}
          data-testid="pnr-print-directions"
        >
          <Map className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('utility.print.directions')}
        </Button>
        </>
        ) : null}

        {showComms ? (
        <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant={compact ? 'ghost' : 'outline'} {...(cls ? { className: cls } : {})} disabled={u.resendPending} data-testid="pnr-resend-info">
              {u.resendPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <MessageSquareShare className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t('utility.send.trigger')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => runResend()} data-testid="pnr-resend-preferred">
              {t('utility.send.preferred')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => runResend('SMS')}>
              {t('utility.send.sms')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => runResend('WHATSAPP')}>
              {t('utility.send.whatsapp')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => runResend('EMAIL')}>
              {t('utility.send.email')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          size="sm"
          variant={compact ? 'ghost' : 'outline'}
          {...(cls ? { className: cls } : {})}
          disabled={u.resendPending}
          title={t('utility.call.hint')}
          onClick={runCall}
          data-testid="pnr-call-passenger"
        >
          {u.resendPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Phone className="mr-1 h-3.5 w-3.5" aria-hidden />
          )}
          {t('utility.call.label')}
        </Button>

        <Button
          size="sm"
          variant={compact ? 'ghost' : 'outline'}
          {...(cls ? { className: cls } : {})}
          onClick={u.openContact}
          data-testid="pnr-update-contact"
        >
          <ContactRound className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('utility.updateContact')}
        </Button>
        </>
        ) : null}
      </div>

      {u.status}
      {u.overlays}
    </section>
  );
}

